import * as React from 'react'

import { observer } from 'mobx-react'

import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Radio,
  RadioGroup,
} from '~/client/src/shared/components/shadcn-ui/radio-group'
import TimeRangeField from '~/client/src/shared/components/TimeRangeField'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { ProjectScheduleMode } from '~/client/src/shared/utils/projectSchedule'

import ProjectSettingsStore from '../../ProjectSettings.store'
import { SettingsField, SettingsFormSection } from '../SettingsFormSection'

interface IProps {
  store: ProjectSettingsStore
  projectDateStore: ProjectDateStore
}

const TOGGLE_ROW_CLASS = 'tw:flex tw:cursor-pointer tw:items-center tw:gap-2'

function ProjectScheduleSection({ store, projectDateStore }: IProps) {
  const { daySchedules, scheduleMode } = store
  const { weekdayNamesList } = projectDateStore

  const isEveryday = scheduleMode === ProjectScheduleMode.Everyday
  // Everyday holds the whole week on one window, so any day carries it.
  const [everydayWindow] = daySchedules

  const getDayName = (dayNumber: number) => weekdayNamesList[dayNumber - 1]

  return (
    <SettingsFormSection title={Localization.translator.schedule}>
      <RadioGroup
        className="tw:w-full tw:gap-4"
        value={scheduleMode}
        onValueChange={value =>
          store.setScheduleMode(value as ProjectScheduleMode)
        }
      >
        <SettingsField
          isMuted={!isEveryday}
          label={
            <label className={TOGGLE_ROW_CLASS}>
              <Radio value={ProjectScheduleMode.Everyday} />
              {Localization.translator.everyday}
            </label>
          }
        >
          {isEveryday && everydayWindow && (
            <TimeRangeField
              startTime={everydayWindow.startTime}
              endTime={everydayWindow.endTime}
              projectDateStore={projectDateStore}
              onChange={store.setEverydayWorkingHours}
              ariaLabel={Localization.translator.defaultWorkingHours}
            />
          )}
        </SettingsField>

        <SettingsField
          isMuted={isEveryday}
          label={
            <label className={TOGGLE_ROW_CLASS}>
              <Radio value={ProjectScheduleMode.Custom} />
              {Localization.translator.custom}
            </label>
          }
        />

        {!isEveryday && (
          <div className="tw:flex tw:w-full tw:flex-col tw:gap-4 tw:pl-6">
            {daySchedules.map(day => (
              <SettingsField
                key={day.dayNumber}
                isMuted={!day.isWorking}
                label={
                  <label className={TOGGLE_ROW_CLASS}>
                    <Checkbox
                      checked={day.isWorking}
                      onCheckedChange={() =>
                        store.toggleWorkingDay(day.dayNumber)
                      }
                    />
                    {getDayName(day.dayNumber)}
                  </label>
                }
              >
                {day.isWorking && (
                  <TimeRangeField
                    startTime={day.startTime}
                    endTime={day.endTime}
                    projectDateStore={projectDateStore}
                    onChange={(startDate, endDate) =>
                      store.setDayWorkingHours(
                        day.dayNumber,
                        startDate,
                        endDate,
                      )
                    }
                    ariaLabel={getDayName(day.dayNumber)}
                  />
                )}
              </SettingsField>
            ))}
          </div>
        )}
      </RadioGroup>
    </SettingsFormSection>
  )
}

export default observer(ProjectScheduleSection)
